import React, { useRef, useEffect } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import axios from "axios";
import styles from "./StopMenu.module.scss";
import { DEV } from "../../../../services/constants";

const StopMenu = ({ visible, onHide, selectedStop, deliveryStops, setDeliveryStops, driverLocation, deliveryId, position }) => {
    const overlayRef = useRef(null);
    const toast = useRef(null);

    // FIXED: Show/hide overlay based on visible prop
    useEffect(() => {
        if (visible && overlayRef.current && selectedStop) {
            // Create a fake event target at the menu position
            const fakeTarget = document.createElement("div");
            fakeTarget.style.position = "fixed";
            fakeTarget.style.left = position.x + "px";
            fakeTarget.style.top = position.y + "px";
            fakeTarget.style.width = "1px";
            fakeTarget.style.height = "1px";
            fakeTarget.style.visibility = "hidden";
            document.body.appendChild(fakeTarget);

            const fakeEvent = { currentTarget: fakeTarget };
            overlayRef.current.show(fakeEvent);

            // Cleanup
            setTimeout(() => {
                if (document.body.contains(fakeTarget)) {
                    document.body.removeChild(fakeTarget);
                }
            }, 100);
        } else if (!visible && overlayRef.current) {
            overlayRef.current.hide();
        }
    }, [visible, position, selectedStop]);

    const moveStopToEnd = async () => {
        if (!selectedStop) return;

        try {
            if (!driverLocation) {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Location Required",
                        detail: "Driver location needed for route optimization",
                        life: 3000,
                    });
                }
                return;
            }

            const updatedStops = deliveryStops.filter((stop) => stop._id !== selectedStop._id);
            const stopToMove = { ...selectedStop, stopNumber: updatedStops.length + 1 };

            const renumberedStops = updatedStops.map((stop, index) => ({
                ...stop,
                stopNumber: index + 1,
            }));

            const finalStops = [...renumberedStops, stopToMove];

            const response = await axios.patch(
                `${DEV}/delivery/${deliveryId}/reorder-stops`,
                {
                    stops: finalStops.map((stop) => ({
                        stopId: stop._id,
                        stopNumber: stop.stopNumber,
                    })),
                    currentLocation: {
                        latitude: driverLocation.lat,
                        longitude: driverLocation.lng,
                    },
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.status === 200 && response.data.success) {
                const { stops: updatedStopsFromAPI } = response.data.data;
                setDeliveryStops(updatedStopsFromAPI);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Stop Moved",
                        detail: `${selectedStop.clientName} moved to end and route optimized`,
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error moving stop:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Move Failed",
                    detail: error.response?.data?.message || "Failed to move stop",
                    life: 3000,
                });
            }
        }

        onHide();
    };

    const callClient = () => {
        if (selectedStop) {
            const phoneNumber = selectedStop.phone || selectedStop.clientPhone;
            if (phoneNumber) {
                window.location.href = `tel:${phoneNumber}`;
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "No Phone Number",
                        detail: `No phone number available for ${selectedStop.clientName}`,
                        life: 3000,
                    });
                }
            }
        }
        onHide();
    };

    const markAsProblem = async () => {
        if (!selectedStop) return;

        try {
            const response = await axios.patch(
                `${DEV}/delivery/stop/${selectedStop._id}/status`,
                {
                    status: "Incomplete",
                    incompleteReason: "Customer not available",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            if (response.status === 200 && response.data.success) {
                setDeliveryStops((prevStops) => prevStops.map((stop) => (stop._id === selectedStop._id ? { ...stop, status: "Incomplete", incompleteReason: "Customer not available", updatedAt: new Date().toISOString() } : stop)));

                if (toast.current) {
                    toast.current.show({
                        severity: "warn",
                        summary: "Stop Marked",
                        detail: `${selectedStop.clientName} marked as problematic`,
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            console.error("Error marking stop as problem:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Update Failed",
                    detail: error.response?.data?.message || "Failed to update stop",
                    life: 3000,
                });
            }
        }

        onHide();
    };

    const getDirections = () => {
        if (selectedStop && driverLocation) {
            const url = `https://maps.google.com/dir/${driverLocation.lat},${driverLocation.lng}/${selectedStop.latitude},${selectedStop.longitude}`;
            window.open(url, "_blank");
        }
        onHide();
    };

    if (!selectedStop) return null;

    return (
        <>
            <Toast ref={toast} />
            <OverlayPanel ref={overlayRef} onHide={onHide} className={styles.stopMenuOverlay}>
                <div className={styles.menuContent}>
                    <div className={styles.menuHeader}>
                        <h4>{selectedStop.clientName}</h4>
                        <small>{selectedStop.email || "No email"}</small>
                    </div>

                    <div className={styles.menuActions}>
                        <Button label="Get Directions" icon="pi pi-directions" text className={styles.menuButton} onClick={getDirections} />

                        <Button label="Call Client" icon="pi pi-phone" text className={styles.menuButton} onClick={callClient} />

                        <Button label="Move to End" icon="pi pi-arrow-down" text className={styles.menuButton} onClick={moveStopToEnd} />

                        <Button label="Mark as Problem" icon="pi pi-exclamation-triangle" text className={styles.menuButtonDanger} onClick={markAsProblem} />
                    </div>
                </div>
            </OverlayPanel>
        </>
    );
};

export default StopMenu;
